import { Injectable, inject } from '@angular/core';
import { JWT_STORAGE_KEY } from '../tenant/auth.service';
import { SessionService } from '../tenant/session.service';
import { FeatureModule } from '../tenant/feature.model';
import { OFFLINE_DB, OfflineDb, MetaEntry, db } from '../offline/db';
import {
  CLAVE_DEL_DEMO, MODULOS_DEL_DEMO, NEGOCIO_DEL_DEMO, NOMBRE_DEL_NEGOCIO_DEL_DEMO, estaEnElDemo, tokenDelDemo,
} from './demo';

/** Dónde se guarda la foto de antes de entrar. Es una clave más de `meta`: al restaurar la foto, desaparece sola. */
const CLAVE_DE_LA_FOTO = 'demo.fotoDeAntes';

interface FotoDeAntes {
  meta: MetaEntry[];
  almacenamiento: Record<string, string>;
}

/**
 * Entrar y salir del demo local (ver `demo.ts`).
 *
 * Condición de ECM: al salir, **nada del demo** queda en este equipo. Y en este equipo puede haber ventas pendientes de
 * un cajero de verdad (la cola sin conexión de otro negocio), así que NO se borra la base entera:
 *  - de las tablas con `tenantId`, solo las filas del negocio del demo;
 *  - `meta` y `localStorage` vuelven a la foto tomada al entrar. Así da igual con qué forma guarde cada servicio sus
 *    claves (unas llevan el negocio dentro, `localOrderSeq` es de todo el equipo): mientras dura el demo no hay sesión
 *    de verdad que pueda escribir nada que merezca quedarse.
 */
@Injectable({ providedIn: 'root' })
export class DemoService {
  private readonly session = inject(SessionService);
  private readonly base: OfflineDb = inject(OFFLINE_DB, { optional: true }) ?? db;

  activo(): boolean {
    return estaEnElDemo();
  }

  async entrar(): Promise<void> {
    if (this.activo()) return;
    // Entrar al demo es cerrar la sesión que hubiera: el demo no convive con un negocio de verdad.
    this.session.logout();
    const foto: FotoDeAntes = { meta: await this.base.meta.toArray(), almacenamiento: this.leerAlmacenamiento() };
    await this.base.meta.put({ key: CLAVE_DE_LA_FOTO, value: foto });
    try {
      localStorage.setItem(CLAVE_DEL_DEMO, '1');
      localStorage.setItem(JWT_STORAGE_KEY, tokenDelDemo());
    } catch {
      /* sin almacenamiento el demo no arranca: `activo()` seguirá en false y la guarda manda al login */
    }
    this.session.setSession({
      tenantId: NEGOCIO_DEL_DEMO,
      tenantName: NOMBRE_DEL_NEGOCIO_DEL_DEMO,
      userName: 'Cajero',
      plan: 'pro',
      role: 'cajero',
      modules: [...MODULOS_DEL_DEMO] as FeatureModule[],
    });
  }

  /** Sale del demo y deja el equipo como estaba antes de entrar. Se puede llamar aunque no se esté en el demo. */
  async salir(): Promise<void> {
    const fila = await this.base.meta.get(CLAVE_DE_LA_FOTO);
    const foto = fila?.value as FotoDeAntes | undefined;
    await this.base.transaction('rw', this.base.tables, async () => {
      for (const tabla of this.base.tables) {
        if (tabla.name === 'meta') continue;
        await tabla.filter((r: { tenantId?: unknown }) => r?.tenantId === NEGOCIO_DEL_DEMO).delete();
      }
      if (foto) {
        await this.base.meta.clear();
        await this.base.meta.bulkPut(foto.meta);
      }
    });
    if (foto) {
      this.restaurarAlmacenamiento(foto.almacenamiento);
    }
    try {
      localStorage.removeItem(CLAVE_DEL_DEMO);
    } catch {
      /* noop */
    }
    // Sin foto (se borró a mano, u otra pestaña ya salió) al menos no queda sesión del demo.
    if (!foto || this.session.session()?.tenantId === NEGOCIO_DEL_DEMO) {
      this.session.logout();
    }
  }

  private leerAlmacenamiento(): Record<string, string> {
    const copia: Record<string, string> = {};
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const clave = localStorage.key(i);
        if (clave !== null) copia[clave] = localStorage.getItem(clave) ?? '';
      }
    } catch {
      /* sin almacenamiento no hay nada que restaurar */
    }
    return copia;
  }

  private restaurarAlmacenamiento(copia: Record<string, string>): void {
    try {
      for (const clave of Object.keys(this.leerAlmacenamiento())) {
        if (!(clave in copia)) localStorage.removeItem(clave);
      }
      for (const [clave, valor] of Object.entries(copia)) {
        localStorage.setItem(clave, valor);
      }
    } catch {
      /* noop */
    }
  }
}
